<template>
  <div :id="`question_${index + 1}`"
       class="py-4 flex"
       :class="[`type-${type}`]"
  >
    <span class="w-12 py-2 px-4 text-3xl leading-relaxed absolute lg:w-20 lg:py-4 lg:px-6"
          :class="{
            'bg-exam-mark-correct': correct === 'correct',
            'bg-exam-mark-wrong': correct === 'wrong',
            'bg-exam-mark-part': correct === 'part',
          }"
    >{{ index + 1 }}</span>
    <div :class="[
      'min-w-0 py-3 flex-1 lg:pl-20 lg:py-5',
      [ isMobile ? 'pl-20' : 'pl-14' ],
    ]">
      <template>
        <div class="-ml-20 pl-20 flex items-center gap-x-1">
          <div v-if="isCreate || isCopy" class="handleQuestion w-10 h-10 my-5 bg-drag flex justify-center items-center absolute -left-5 box-content cursor-grab transform -translate-y-1"></div>
          <GrmTextField :placeholder="isMobile ? $t('testBank.alert.shortName') : $t('testBank.alert.name')" v-model="question.questionTitle" class="min-w-0 flex-1 transition" :disabled="isView" />
          <div v-if="testbankObj.examType === 'QUIZ'" class="flex items-center gap-x-0.5">
            <GrmTextField type="number" placeholder="1" v-model="question.questionScore" class="w-20" :min="1" :max="100" :disabled="!isCreate && !isCopy" />
            <span class="text-xs">{{ $t('exam.question.point') }}</span>
          </div>
          <div v-if="isCreate || isCopy">
            <GrmBtn size="xs"
                    color="red"
                    @click="$emit('deleteQuestion', question)"
            >{{ $t("common.delete") }}</GrmBtn>
          </div>
        </div>
      </template>
      <div class="mt-2 flex flex-col gap-y-1 relative">
        <draggable v-model="question.answerList"
                   v-bind="dragOptions"
                   @start="onDragStart"
                   @end="onDragEnd"
                   @update="() => onDragUpdate(question.answerList)"
        >
          <div v-for="(answer, i) in question.answerList"
               :key="i"
               class="flex items-center gap-x-1"
          >
            <div v-if="isCreate || isCopy" class="handleAnswer w-10 h-10 bg-drag flex justify-center items-center absolute -left-10 box-content cursor-grab"></div>
            <div class="question min-w-0 max-w-max flex-1 rounded py-1 flex gap-x-2"
                 :class="[ isEdit ? 'items-center' : 'items-baseline' ]"
                 @click="selectQuestion($event, answer)"
            >
              <div class="relative">
                <button class="number w-5 h-5 border rounded-full flex justify-center items-center text-sm select-none transition"
                        :class="[
                          (testbankObj.examType === 'QUIZ') && answer.correct && (isCreate || isEdit || isCopy)
                          || (testbankExam.examType === 'QUIZ') && answer.correct && (isView)
                            ? 'border-blue-400 bg-blue-400 text-white'
                            : 'border-gray-800',
                          { 'pointer-events-none':
                            (testbankObj.examType === 'SURVEY' && isCreate)
                            || (testbankObj.examType === 'SURVEY' && isCopy)
                            || isEdit
                            || (isView)
                          },
                        ]"
                >{{ i + 1 }}</button>
                <span v-if="false" class="w-5 h-5 bg-exam-check-red block absolute -top-2.5 -left-0.5 pointer-events-none" />
              </div>
              <GrmTextField size="xs" class="w-60" :placeholder="$t('testBank.add.placeholder')" v-model="answer.answerTitle" :disabled="isView" />
            </div>
            <GrmBtn v-if="isCreate || isCopy"
                    size="xs"
                    color="red"
                    @click="onDeleteAnswer(answer)"
            >{{ $t("common.delete") }}</GrmBtn>
          </div>
        </draggable>
        <GrmBtn v-if="isCreate || isCopy" @click="onAddAnswer">{{ $t("testBank.add.select") }}</GrmBtn>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";

import GrmBtn from "@/components/Common/button/GrmBtn.vue";
import GrmTextField from "@/components/Common/textField/GrmTextField.vue";
import {Device} from "@/utils";

export default {
  name: 'TestbankQuestionChoice',
  components: {
    GrmBtn,
    GrmTextField
  },
  props: {
    type: { type: String, default: 'view' },
    index: { type: Number, default: 0 },
    correct: { type: String, default: '' },
    question: { type: Object, default: () => ({}), required: true },
    testbankObj: { type: Object, default: () => ({}) },
  },
  computed: {
    ...mapState('testbank', [
      'view',
      'testbankExam',
    ]),
    ...mapGetters('testbank', [
      'isCreate',
      'isEdit',
      'isCopy',
      'isView'
    ]),

    dragOptions() {
      return {
        animation: 200,
        group: 'answer',
        ghostClass: 'opacity-20',
        handle: '.handleAnswer',
      };
    },
    isMobile() {
      return Device.isMobile();
    }
  },
  methods: {
    selectQuestion(e, answer) {
      if (!this.isCreate && !this.isCopy) {
        return;
      }

      if (this.isCreate || this.isEdit || this.isCopy) {
        const isQuestionNumber = e.target.classList.contains('number');
        if (!isQuestionNumber) {
          return;
        }
      }

      answer.correct = !answer.correct;
      this.question.correctAnswerCnt = this.question.answerList.filter(answer => answer.correct).length;
    },
    onDragStart() {
      this.$root.$el.classList.add('cursor-grabbing');
    },
    onDragEnd() {
      this.$root.$el.classList.remove('cursor-grabbing');
    },
    onDragUpdate(list) {
      list.map((item, index) => item.answerOrder = index);
    },
    onAddAnswer() {
      if (this.question.answerList.length === 10) {
        return;
      }

      this.question.answerList.push({
        answerTitle: '',
        correct: false,
        answerOrder: this.question.answerList.length,
      });
    },
    onDeleteAnswer(answer) {
      if (this.question.answerList.length === 2) {
        return;
      }

      const index = this.question.answerList.indexOf(answer);
      this.question.answerList.splice(index, 1);
      this.question.correctAnswerCnt = this.question.answerList.filter(answer => answer.correct).length;
    },
  }
}
</script>

<style lang="scss" scoped>
.type-view {}
.type {
  &-solve {
    .question {
      &:hover {
        .number { @apply border-blue-600 bg-blue-600 text-white; }
      }
    }
  }
}
</style>